import { useLazyQuery, useQuery } from '@apollo/client/react'
import { ArrowUpDown, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_GROUPS } from '~/gql/groups/groups'
import { GQL_SCORM_TRACKINGS } from '~/gql/scorm/scorm'
import { GQL_SESSIONS } from '~/gql/sessions/sessions'
import { client } from '~/helpers/apollo'
import { EXPORT_MAX_ROWS, fetchAllForExport } from '~/pages/backoffice/exportPagination'
import TablePagination from '~/pages/backoffice/TablePagination'
import { useTableQuery } from '~/pages/backoffice/useTableQuery'
import { downloadCSV } from '~/utils/exportCsv/utils'
import StatisticsDashboard from './StatisticsDashboard'

// Plafond d'heures plausible (1 an) : au-delà, la durée est aberrante (donnée
// legacy corrompue) et on n'affiche rien plutôt qu'un "666667h".
const MAX_PLAUSIBLE_HOURS = 366 * 24

function formatDuration(iso: string | null | undefined): string {
  if (!iso) return '-'
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
  if (!match) return iso
  if (match[1] && Number.parseInt(match[1], 10) > MAX_PLAUSIBLE_HOURS) return '-'
  const h = match[1] ? `${match[1]}h ` : ''
  const m = match[2] ? `${match[2]}m ` : ''
  const s = match[3] ? `${match[3]}s` : ''
  return `${h}${m}${s}`.trim() || '-'
}

// Affiche "raw / max" arrondi, sans notation scientifique. Renvoie null si une
// valeur est absente ou aberrante (donnée legacy corrompue, ex. 3.99e+49).
function formatScore(raw: number | null | undefined, max: number | null | undefined): string | null {
  if (raw == null || max == null) return null
  if (!Number.isFinite(raw) || !Number.isFinite(max)) return null
  if (Math.abs(raw) > 1_000_000 || Math.abs(max) > 1_000_000) return null
  const fmt = (n: number) => Number.parseFloat(n.toFixed(2)).toString()
  return `${fmt(raw)} / ${fmt(max)}`
}

// Export CSV générique (produit white-label) : en-têtes construits via i18n.

function completionLabelKey(status: string | null | undefined): string {
  switch (status) {
    case 'COMPLETED': return 'status.completed'
    case 'INCOMPLETE': return 'status.incomplete'
    case 'NOT_ATTEMPTED': return 'status.not_attempted'
    default: return 'status.unknown'
  }
}

function successLabelKey(status: string | null | undefined): string {
  switch (status) {
    case 'PASSED': return 'status.passed'
    case 'FAILED': return 'status.failed'
    default: return 'status.unknown'
  }
}

function formatDateFr(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function StatisticsTable() {
  const [searchParams] = useSearchParams()
  const { page, pageSize, setPage, resetPage, handleSort, queryArgs } = useTableQuery()
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  // Amorçable par l'URL (`?sessionId=…`) : l'accueil (#201) y envoie depuis
  // chacun de ses faits, qui portent tous sur UN parcours précis. Sans ça, le
  // lien ouvrirait la promo entière et la question posée trois lignes plus haut
  // resterait à reformuler à la main.
  //
  // Lu une seule fois, à l'ouverture : le sélecteur reprend la main ensuite, et
  // ne réécrit pas l'URL. Une session inconnue (parcours supprimé, lien collé
  // depuis ailleurs) laisse simplement le sélecteur sur « toutes », plutôt que
  // d'afficher un filtre actif dont le nom n'apparaît nulle part.
  const [requestedSession, setSessionFilter] = useState(() => searchParams.get('sessionId') ?? '')
  const [groupFilter, setGroupFilter] = useState('')
  // Le bouton « rafraîchir » doit recharger la table ET ses agrégats, qui vivent
  // dans un composant voisin avec sa propre requête.
  const [reloadToken, setReloadToken] = useState(0)
  const { t } = useTranslation()

  const { data: sessionsData } = useQuery(GQL_SESSIONS, { client })
  const { data: groupsData } = useQuery(GQL_GROUPS, { client })
  const sessions = sessionsData?.sessions ?? []
  const groups = groupsData?.groups ?? []

  // Le parcours amorcé par l'URL n'existe peut-être plus. Le garder filtrerait
  // sur un id absent du sélecteur : des chiffres restreints, et aucune mention à
  // l'écran de ce qui les restreint. On retombe donc sur « tous les parcours ».
  //
  // Dérivé plutôt que corrigé dans un effet : tant que la liste n'est pas
  // arrivée, le filtre reste appliqué — le remettre à zéro « en attendant »
  // ferait clignoter les chiffres de toute l'organisation sur un écran qu'on a
  // ouvert pour une promo précise.
  const sessionFilter = sessions.length === 0 || sessions.some(s => s.id === requestedSession)
    ? requestedSession
    : ''

  // Les deux filtres se cumulent : « le client A, sur la session de mars ».
  const filterBy = sessionFilter || groupFilter
    ? {
        ...(sessionFilter ? { sessionId: sessionFilter } : {}),
        ...(groupFilter ? { groupId: groupFilter } : {}),
      }
    : undefined

  // Débounce la recherche : la requête ne part qu'après une pause de saisie,
  // pour éviter une requête par frappe (et le ThrottlerException associé).
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText)
      resetPage()
    }, 400)
    return () => clearTimeout(handler)
  }, [searchText, resetPage])

  // cache-first : revenir sur une page déjà chargée est servi depuis le cache
  // (zéro requête réseau), ce qui évite de saturer le throttler en navigation rapide.
  const { data: currentData, previousData, refetch, loading } = useQuery(GQL_SCORM_TRACKINGS, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        ...queryArgs,
        searchInput: debouncedSearch,
        filterBy,
      },
    },
  })

  // Conserve l'affichage précédent tant que la nouvelle page charge ou en cas
  // d'erreur ponctuelle (ex. throttle) : le tableau ne se vide plus brutalement.
  const data = currentData ?? previousData

  // network-only : l'export part en plusieurs tranches, qui doivent toutes venir
  // de la même lecture serveur — une tranche servie depuis le cache mélangerait
  // deux états de la base dans un même fichier.
  const [fetchExportedTrackings] = useLazyQuery(GQL_SCORM_TRACKINGS, { fetchPolicy: 'network-only' })

  async function exportCsv() {
    const exported = await fetchAllForExport(async (skip, take) => {
      const chunk = await fetchExportedTrackings({
        variables: {
          input: {
            skip,
            take,
            searchInput: debouncedSearch,
            filterBy,
          },
        },
      })
      const result = chunk.data?.getScormTrackings
      return result?.trackings ? { rows: result.trackings, total: result.total } : null
    })
    if (!exported) return

    if (exported.truncated) {
      toast.warning(t('export.truncated', { max: EXPORT_MAX_ROWS, total: exported.total }))
    }

    downloadCSV(
      t('common.statistics'),
      [
        t('stats.export.email'),
        t('common.firstname'),
        t('common.lastname'),
        t('common.session'),
        t('stats.export.completion'),
        t('stats.export.success'),
        t('common.score'),
        t('common.totalTime'),
        t('stats.export.completedAt'),
      ],
      exported.rows.map((tr) => {
        const score = tr.scoreRaw != null && tr.scoreMax != null
          ? `${tr.scoreRaw}/${tr.scoreMax}`
          : ''
        const totalTime = tr.totalTime ? formatDuration(tr.totalTime) : ''
        const validation = formatDateFr(tr.completedAt as unknown as string | null | undefined)
        return [
          /* Email */ tr.userEmail ?? '',
          /* Prénom */ tr.userFirstName ?? '',
          /* Nom */ tr.userLastName ?? '',
          /* Session */ tr.sessionName ?? '',
          /* Complétion */ t(completionLabelKey(tr.completionStatus)),
          /* Réussite */ t(successLabelKey(tr.successStatus)),
          /* Score */ score,
          /* Temps total */ totalTime,
          /* Complété le */ validation,
        ]
      }),
    )
  }

  const getCompletionBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge variant="success">{t('status.completed')}</Badge>
      case 'INCOMPLETE':
        return <Badge variant="warning">{t('status.incomplete')}</Badge>
      case 'NOT_ATTEMPTED':
        return <Badge variant="secondary">{t('status.not_attempted')}</Badge>
      default:
        return <Badge variant="secondary">{t('status.unknown')}</Badge>
    }
  }

  const getSuccessBadge = (status: string) => {
    switch (status) {
      case 'PASSED':
        return <Badge variant="success">{t('status.passed')}</Badge>
      case 'FAILED':
        return <Badge variant="destructive">{t('status.failed')}</Badge>
      default:
        return <Badge variant="secondary">{t('status.unknown')}</Badge>
    }
  }

  return (
    <div className="space-y-4">
      <StatisticsDashboard
        searchInput={debouncedSearch}
        sessionId={sessionFilter}
        groupId={groupFilter}
        reloadToken={reloadToken}
      />

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Input
            placeholder={t('common.search')}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="max-w-sm"
          />
          <select
            value={sessionFilter}
            onChange={(e) => {
              setSessionFilter(e.target.value)
              resetPage()
            }}
            className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
            aria-label={t('common.sessions')}
          >
            <option value="">{t('common.allSessions')}</option>
            {sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select
            value={groupFilter}
            onChange={(e) => {
              setGroupFilter(e.target.value)
              resetPage()
            }}
            className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
            aria-label={t('common.groups')}
          >
            <option value="">{t('groups.all')}</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <Button
            variant="outline"
            onClick={() => {
              void refetch()
              setReloadToken(token => token + 1)
            }}
          >
            <RefreshCw className="size-4" />
          </Button>
        </div>
        <Button variant="outline" onClick={exportCsv}>
          {t('common.export')}
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('userEmail')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.email')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('userFirstName')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.firstname')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('userLastName')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.lastname')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>{t('common.session')}</TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('completionStatus')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.completionStatus')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('successStatus')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.successStatus')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('scoreRaw')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.score')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('completedAt')} className="p-0 h-8 w-full justify-start text-left font-medium">
                  {t('common.completedAt')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                {t('common.totalTime')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading
              ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-24 text-center">
                      {t('common.loading')}
                      ...
                    </TableCell>
                  </TableRow>
                )
              : !data?.getScormTrackings.trackings?.length
                  ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center">
                          {t('common.empty')}
                        </TableCell>
                      </TableRow>
                    )
                  : (
                      data.getScormTrackings.trackings.map(tr => (
                        <TableRow key={tr.id}>
                          <TableCell>{tr.userEmail}</TableCell>
                          <TableCell>{tr.userFirstName}</TableCell>
                          <TableCell>{tr.userLastName}</TableCell>
                          <TableCell>{tr.sessionName}</TableCell>
                          <TableCell>{getCompletionBadge(tr.completionStatus)}</TableCell>
                          <TableCell>{getSuccessBadge(tr.successStatus)}</TableCell>
                          <TableCell>
                            {formatScore(tr.scoreRaw, tr.scoreMax) ?? '-'}
                          </TableCell>
                          <TableCell>
                            {tr.completedAt
                              ? new Date(tr.completedAt as unknown as string).toLocaleDateString('fr-FR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })
                              : '-'}
                          </TableCell>
                          <TableCell>{formatDuration(tr.totalTime)}</TableCell>
                        </TableRow>
                      ))
                    )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        page={page}
        pageSize={pageSize}
        total={data?.getScormTrackings.total}
        onPageChange={setPage}
      />
    </div>
  )
}

export default StatisticsTable
